import React, { useMemo, useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { KPICard } from '../../components/ui/KPICard';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { contracts, Contract } from '../../data/contracts';
import { FileSignatureIcon, AlertCircleIcon, CheckCircleIcon, ArrowLeftIcon, DownloadIcon, MessageSquareIcon, PenToolIcon, FileTextIcon, ClockIcon } from 'lucide-react';
export const ContractsPage: React.FC = () => {
  const {
    user
  } = useAuth();
  const supplierId = 'SUP-001'; // Default for demo
  const [activeTab, setActiveTab] = useState('all');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  // Filter contracts for this supplier
  const myContracts = useMemo(() => {
    return contracts.filter(c => c.supplierId === supplierId);
  }, [supplierId]);
  // Filter based on active tab
  const filteredContracts = useMemo(() => {
    if (activeTab === 'all') return myContracts;
    if (activeTab === 'action-required') return myContracts.filter(c => ['supplier-review', 'pending-signature', 'negotiation'].includes(c.status));
    if (activeTab === 'active') return myContracts.filter(c => ['active', 'expiring-soon'].includes(c.status));
    return myContracts;
  }, [myContracts, activeTab]);
  // KPIs
  const actionRequiredCount = myContracts.filter(c => ['supplier-review', 'pending-signature', 'negotiation'].includes(c.status)).length;
  const activeCount = myContracts.filter(c => ['active', 'expiring-soon'].includes(c.status)).length;
  const expiringCount = myContracts.filter(c => c.status === 'expiring-soon').length;
  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    if (amount === 0) return 'N/A';
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  // Format contract type
  const formatType = (type: string) => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  // Detail View
  if (selectedContract) {
    return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-974">
        <div className="flex items-center gap-2 text-sm text-text-secondary mb-4 cursor-pointer hover:text-fpg-navy transition-colors" onClick={() => setSelectedContract(null)} data-id="element-975">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-976" />
          Back to Contracts
        </div>

        <PageHeader title={selectedContract.title} description={`${selectedContract.id} • Version ${selectedContract.version} • Last updated ${selectedContract.lastUpdated}`} primaryAction={['supplier-review', 'pending-signature', 'negotiation'].includes(selectedContract.status) ? <Button variant="primary" leftIcon={<PenToolIcon className="w-4 h-4" data-id="element-979" />} data-id="element-978">
                Review & Sign
              </Button> : <Button variant="secondary" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-981" />} data-id="element-980">
                Download PDF
              </Button>} secondaryActions={<Button variant="secondary" leftIcon={<MessageSquareIcon className="w-4 h-4" data-id="element-983" />} data-id="element-982">
              Message Owner
            </Button>} data-id="element-977" />

        {['supplier-review', 'pending-signature', 'negotiation'].includes(selectedContract.status) && <div className="bg-status-warning-bg border border-status-warning/20 rounded-fluent-lg p-4 flex items-start gap-3" data-id="element-984">
            <AlertCircleIcon className="w-5 h-5 text-status-warning mt-0.5 flex-shrink-0" data-id="element-985" />
            <div data-id="element-986">
              <h3 className="text-sm font-semibold text-status-warning mb-1" data-id="element-987">
                Action Required
              </h3>
              <p className="text-sm text-text-secondary" data-id="element-988">
                This contract requires your review and signature. Please review
                the terms and any clause deviations before accepting.
              </p>
              <div className="mt-3 flex gap-3" data-id="element-989">
                <Button variant="primary" size="sm" data-id="element-990">
                  Accept & Sign
                </Button>
                <Button variant="secondary" size="sm" data-id="element-991">
                  Propose Redlines
                </Button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-992">
          <Card className="md:col-span-2 space-y-6" data-id="element-993">
            <h2 className="text-lg font-semibold text-text-primary border-b border-border-light pb-3" data-id="element-994">
              Contract Details
            </h2>

            <div className="grid grid-cols-2 gap-y-6 gap-x-4" data-id="element-995">
              <div data-id="element-996">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-997">
                  Status
                </p>
                <StatusPill status={selectedContract.status} data-id="element-998" />
              </div>
              <div data-id="element-999">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1000">
                  Contract Type
                </p>
                <p className="text-sm text-text-primary font-medium" data-id="element-1001">
                  {formatType(selectedContract.type)}
                </p>
              </div>
              <div data-id="element-1002">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1003">
                  Start Date
                </p>
                <p className="text-sm text-text-primary" data-id="element-1004">
                  {selectedContract.startDate}
                </p>
              </div>
              <div data-id="element-1005">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1006">
                  End Date
                </p>
                <p className="text-sm text-text-primary font-medium" data-id="element-1007">
                  {selectedContract.endDate}
                </p>
              </div>
              <div data-id="element-1008">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1009">
                  Contract Value
                </p>
                <p className="text-lg font-bold text-text-primary" data-id="element-1010">
                  {formatCurrency(selectedContract.value, selectedContract.currency)}
                </p>
              </div>
              <div data-id="element-1011">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1012">
                  Renewal Terms
                </p>
                <p className="text-sm text-text-primary capitalize" data-id="element-1013">
                  {selectedContract.renewalType}
                </p>
              </div>
            </div>
          </Card>

          <Card className="space-y-6" data-id="element-1014">
            <h2 className="text-lg font-semibold text-text-primary border-b border-border-light pb-3" data-id="element-1015">
              FPG Contacts
            </h2>
            <div className="space-y-4" data-id="element-1016">
              <div data-id="element-1017">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1018">
                  Business Owner
                </p>
                <p className="text-sm text-text-primary font-medium" data-id="element-1019">
                  {selectedContract.owner}
                </p>
                <p className="text-xs text-text-secondary" data-id="element-1020">
                  {selectedContract.department}
                </p>
              </div>
              <div data-id="element-1021">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1022">
                  Legal Counsel
                </p>
                <p className="text-sm text-text-primary font-medium" data-id="element-1023">
                  FPG Legal Team
                </p>
                <Button variant="ghost" size="sm" className="mt-1 -ml-3 text-fpg-navy" data-id="element-1024">
                  Contact Legal
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <Card noPadding data-id="element-1025">
          <div className="p-5 border-b border-border-light flex justify-between items-center" data-id="element-1026">
            <h2 className="text-lg font-semibold text-text-primary" data-id="element-1027">
              Documents & Clauses
            </h2>
          </div>
          <div className="p-5" data-id="element-1028">
            <div className="flex items-center justify-between p-4 border border-border-light rounded-fluent bg-surface-secondary mb-4" data-id="element-1029">
              <div className="flex items-center gap-3" data-id="element-1030">
                <FileTextIcon className="w-6 h-6 text-fpg-navy" data-id="element-1031" />
                <div data-id="element-1032">
                  <h4 className="font-medium text-text-primary" data-id="element-1033">
                    Main Agreement Document
                  </h4>
                  <p className="text-xs text-text-secondary" data-id="element-1034">
                    PDF • 2.4 MB • Uploaded {selectedContract.lastUpdated}
                  </p>
                </div>
              </div>
              <Button variant="secondary" size="sm" data-id="element-1035">
                Download
              </Button>
            </div>

            {selectedContract.clauseDeviations > 0 ? <div className="mt-6" data-id="element-1036">
                <h3 className="text-sm font-semibold text-text-primary mb-3" data-id="element-1037">
                  Clause Deviations ({selectedContract.clauseDeviations})
                </h3>
                <div className="space-y-3" data-id="element-1038">
                  <div className="p-4 border border-status-warning/30 bg-status-warning-bg/30 rounded-fluent" data-id="element-1039">
                    <div className="flex justify-between items-start mb-2" data-id="element-1040">
                      <h4 className="font-medium text-text-primary text-sm" data-id="element-1041">
                        7.2 Limitation of Liability
                      </h4>
                      <Badge variant="warning" data-id="element-1042">Negotiation</Badge>
                    </div>
                    <p className="text-sm text-text-secondary mb-3" data-id="element-1043">
                      Standard FPG clause modified to cap liability at 2x
                      contract value instead of unlimited.
                    </p>
                    <Button variant="secondary" size="sm" data-id="element-1044">
                      View Redline
                    </Button>
                  </div>
                </div>
              </div> : <div className="mt-6 text-center py-6 border border-border-light border-dashed rounded-fluent" data-id="element-1045">
                <CheckCircleIcon className="w-8 h-8 text-status-success mx-auto mb-2" data-id="element-1046" />
                <p className="text-sm font-medium text-text-primary" data-id="element-1047">
                  Standard Terms
                </p>
                <p className="text-xs text-text-secondary" data-id="element-1048">
                  No clause deviations from standard FPG template.
                </p>
              </div>}
          </div>
        </Card>
      </div>;
  }
  // List View
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-1049">
      <PageHeader title="Contracts" description="Review, negotiate, and sign enterprise agreements with FairPrice Group." breadcrumbs={[{
      label: 'Home',
      href: '/supplier/dashboard'
    }, {
      label: 'Contracts'
    }]} data-id="element-1050" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1051">
        <KPICard title="Action Required" value={actionRequiredCount.toString()} icon={<AlertCircleIcon className="w-5 h-5 text-status-warning" data-id="element-1053" />} trend={actionRequiredCount > 0 ? 'up' : 'neutral'} trendValue={actionRequiredCount > 0 ? 'Pending your review' : 'All caught up'} data-id="element-1052" />
        <KPICard title="Active Contracts" value={activeCount.toString()} icon={<FileSignatureIcon className="w-5 h-5 text-fpg-navy" data-id="element-1055" />} data-id="element-1054" />
        <KPICard title="Expiring Soon" value={expiringCount.toString()} icon={<ClockIcon className="w-5 h-5 text-status-error" data-id="element-1057" />} trend={expiringCount > 0 ? 'down' : 'neutral'} trendValue="Within 90 days" data-id="element-1056" />
      </div>

      <Card noPadding className="flex flex-col overflow-hidden" data-id="element-1058">
        <div className="px-6 pt-4 border-b border-border-light" data-id="element-1059">
          <Tabs tabs={[{
          id: 'all',
          label: 'All Contracts',
          count: myContracts.length
        }, {
          id: 'action-required',
          label: 'Action Required',
          count: actionRequiredCount
        }, {
          id: 'active',
          label: 'Active',
          count: activeCount
        }]} activeTab={activeTab} onChange={setActiveTab} data-id="element-1060" />
        </div>

        <DataTable data={filteredContracts} onRowClick={row => setSelectedContract(row)} columns={[{
        header: 'Contract ID',
        accessorKey: 'id',
        sortable: true
      }, {
        header: 'Title',
        accessorKey: 'title',
        sortable: true
      }, {
        header: 'Type',
        cell: row => formatType(row.type)
      }, {
        header: 'End Date',
        accessorKey: 'endDate',
        sortable: true
      }, {
        header: 'Status',
        cell: row => <StatusPill status={row.status} data-id="element-1062" />
      }]} data-id="element-1061" />
      </Card>
    </div>;
};